import React, {
     useCallback,
     useContext,
     useEffect,
     useMemo,
     useState,
} from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment/moment";

import {
     retailSalesGetWinnerFilterOptionsRequest,
     retailSalesGetWinnersRequest,
} from "../../../../../_redux/_actions";
// Imported from their own files rather than the _constants barrel: the barrel can still be
// half-initialised when this module evaluates, which leaves the constants undefined.
import { contextApiActions } from "../../../../../_redux/_constants/common.constants";
import { retailSalesConstants } from "../../../../../_redux/_constants/retailSales.constants";
import { Context } from "../../../../../_context/Store";
import RetailWinnerDetails from "../forms/RetailWinnerDetails";
import EditRetailWinner from "../forms/EditRetailWinner";

const ALL_OPTION = { id: null, name: "All" };

const ACTIVATION_OPTIONS = [
     { id: null, name: "All" },
     { id: true, name: "Activated" },
     { id: false, name: "Not activated" },
];

const INITIAL_FILTERS = {
     activation: ACTIVATION_OPTIONS[0],
     area: ALL_OPTION,
     dealer: ALL_OPTION,
     jobRole: ALL_OPTION,
     week: ALL_OPTION,
};

const INITIAL_PAGE = { skip: 0, take: 10 };

const RetailWinnersGrid = () => {
     const actionDispatch = useDispatch();
     const [, contextDispatch] = useContext(Context);
     const winnersPage = useSelector((state) => state?.retailSales?.winnerList);
     const winnerFilterOptions = useSelector(
          (state) => state?.retailSales?.winnerFilterOptions,
     );

     // The winners call is in flight while its request action sits in the pending list the loading
     // middleware maintains. Used to mask the grid so a filter change never looks like it did nothing.
     const isLoadingWinners = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               retailSalesConstants.RETAIL_SALES_GET_WINNERS_REQUEST,
          ),
     );

     const [filters, setFilters] = useState(INITIAL_FILTERS);
     const [search, setSearch] = useState("");
     const [page, setPage] = useState(INITIAL_PAGE);
     const [sort, setSort] = useState([]);
     const [optionSearch, setOptionSearch] = useState({
          dealerSearch: "",
          areaSearch: "",
          jobRoleSearch: "",
     });

     // The dropdowns are searchable and the typed text is matched by the backend, so the option
     // lists are re-fetched rather than narrowed here.
     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    retailSalesGetWinnerFilterOptionsRequest({
                         dealerSearch: optionSearch.dealerSearch || undefined,
                         areaSearch: optionSearch.areaSearch || undefined,
                         jobRoleSearch: optionSearch.jobRoleSearch || undefined,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, optionSearch]);

     // Filtering, sorting and paging are all done by the backend: the request carries the criteria
     // and the response is exactly the page that should be shown.
     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    retailSalesGetWinnersRequest({
                         accountActivated: filters.activation.id,
                         retailAreaId: filters.area.id,
                         dealershipId: filters.dealer.id,
                         jobRoleId: filters.jobRole.id,
                         week: filters.week.id || undefined,
                         searchTerm: search.trim() ? search.trim() : undefined,
                         page: Math.floor(page.skip / page.take) + 1,
                         pageSize: page.take,
                         sortBy: sort.length ? sort[0].field : undefined,
                         sortDirection: sort.length ? sort[0].dir : undefined,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, filters, search, page, sort]);

     const areaOptions = useMemo(
          () => [ALL_OPTION, ...(winnerFilterOptions?.areas || [])],
          [winnerFilterOptions],
     );

     const dealerOptions = useMemo(
          () => [
               ALL_OPTION,
               ...(winnerFilterOptions?.dealers || []).map((dealer) => ({
                    id: dealer.id,
                    name: `${dealer.dealerCode} - ${dealer.name}`,
               })),
          ],
          [winnerFilterOptions],
     );

     const jobRoleOptions = useMemo(
          () => [ALL_OPTION, ...(winnerFilterOptions?.jobRoles || [])],
          [winnerFilterOptions],
     );

     // Weeks come back as plain labels already ordered by week number, so they are wrapped in the
     // same {id, name} shape the other dropdowns use. Only weeks that have a reward are offered.
     const weekOptions = useMemo(
          () => [
               ALL_OPTION,
               ...(winnerFilterOptions?.weeks || []).map((week) => ({
                    id: week,
                    name: week,
               })),
          ],
          [winnerFilterOptions],
     );

     const winners = useMemo(() => {
          const list = winnersPage?.winners || [];

          return list.map((winner) => ({
               ...winner,
               name: `${winner.firstName || ""} ${winner.lastName || ""}`.trim(),
               activationStatus: winner.accountActivated
                    ? "Activated"
                    : "Not activated",
               activatedOn: winner.accountActivatedOn
                    ? moment(winner.accountActivatedOn).format("DD/MM/YYYY")
                    : "",
               loggedIn: winner.hasLoggedIn ? "Yes" : "No",
               lastLoginOn: winner.lastLoginOn
                    ? moment(winner.lastLoginOn).format("DD/MM/YYYY")
                    : "",
          }));
     }, [winnersPage]);

     const totalCount = winnersPage?.totalCount || 0;

     const onFilterChange = useCallback((field, value) => {
          setFilters((previous) => ({ ...previous, [field]: value }));
          setPage(INITIAL_PAGE);
     }, []);

     const onSearchChange = useCallback((event) => {
          setSearch(event.value);
          setPage(INITIAL_PAGE);
     }, []);

     const openWinnerDetails = useCallback(
          (winner) => {
               contextDispatch({
                    type: contextApiActions.UI_MODAL_TOGGEL,
                    payload: {
                         show: true,
                         heading: "Winner Details",
                         children: <RetailWinnerDetails winner={winner} />,
                    },
               });
          },
          [contextDispatch],
     );

     // The edit form needs the real dealer and role lists, without the "All" entry the filters use.
     const editDealerOptions = useMemo(
          () =>
               (winnerFilterOptions?.dealers || []).map((dealer) => ({
                    id: dealer.id,
                    label: `${dealer.dealerCode} - ${dealer.name}`,
               })),
          [winnerFilterOptions],
     );

     const editJobRoleOptions = useMemo(
          () => winnerFilterOptions?.jobRoles || [],
          [winnerFilterOptions],
     );

     const openEditWinner = useCallback(
          (winner) => {
               contextDispatch({
                    type: contextApiActions.UI_MODAL_TOGGEL,
                    payload: {
                         show: true,
                         heading: "Edit Winner",
                         children: (
                              <EditRetailWinner
                                   winner={winner}
                                   dealerOptions={editDealerOptions}
                                   jobRoleOptions={editJobRoleOptions}
                                   onDealerSearch={(text) =>
                                        setOptionSearch((previous) => ({
                                             ...previous,
                                             dealerSearch: text,
                                        }))
                                   }
                                   onSaved={() => {
                                        contextDispatch({
                                             type: contextApiActions.UI_MODAL_TOGGEL,
                                             payload: { show: false },
                                        });
                                        setPage((previous) => ({ ...previous }));
                                   }}
                                   onClose={() =>
                                        contextDispatch({
                                             type: contextApiActions.UI_MODAL_TOGGEL,
                                             payload: { show: false },
                                        })
                                   }
                              />
                         ),
                    },
               });
          },
          [contextDispatch, editDealerOptions, editJobRoleOptions],
     );

     const onOptionSearchChange = useCallback((field, event) => {
          setOptionSearch((previous) => ({
               ...previous,
               [field]: event.filter ? event.filter.value : "",
          }));
     }, []);

     const clearFilters = useCallback(() => {
          setFilters(INITIAL_FILTERS);
          setSearch("");
          setSort([]);
          setPage(INITIAL_PAGE);
          setOptionSearch({
               dealerSearch: "",
               areaSearch: "",
               jobRoleSearch: "",
          });
     }, []);

     return (
          <div className="sba-telerik-components-wr">
               <div
                    className="sba-chart-section-top-search"
                    style={{
                         display: "flex",
                         flexDirection: "row",
                         flexWrap: "wrap",
                         alignItems: "flex-end",
                         gap: "12px",
                         width: "100%",
                    }}
               >
                    <Input
                         style={{ width: "260px" }}
                         value={search}
                         onChange={onSearchChange}
                         placeholder="Search name, email or dealer"
                    />
                    <DropDownList
                         style={{ width: "180px" }}
                         label="Activation"
                         data={ACTIVATION_OPTIONS}
                         textField="name"
                         dataItemKey="name"
                         value={filters.activation}
                         onChange={(event) =>
                              onFilterChange("activation", event.value)
                         }
                    />
                    <DropDownList
                         style={{ width: "150px" }}
                         label="Retail area"
                         data={areaOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.area}
                         filterable={true}
                         onFilterChange={(event) =>
                              onOptionSearchChange("areaSearch", event)
                         }
                         onChange={(event) => onFilterChange("area", event.value)}
                    />
                    <DropDownList
                         style={{ width: "280px" }}
                         label="Dealer"
                         data={dealerOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.dealer}
                         filterable={true}
                         onFilterChange={(event) =>
                              onOptionSearchChange("dealerSearch", event)
                         }
                         onChange={(event) =>
                              onFilterChange("dealer", event.value)
                         }
                    />
                    <DropDownList
                         style={{ width: "180px" }}
                         label="Role"
                         data={jobRoleOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.jobRole}
                         filterable={true}
                         onFilterChange={(event) =>
                              onOptionSearchChange("jobRoleSearch", event)
                         }
                         onChange={(event) =>
                              onFilterChange("jobRole", event.value)
                         }
                    />
                    <DropDownList
                         style={{ width: "140px" }}
                         label="Week"
                         data={weekOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.week}
                         onChange={(event) =>
                              onFilterChange("week", event.value)
                         }
                    />
                    <Button type="button" onClick={clearFilters}>
                         Clear
                    </Button>
               </div>
               <div style={{ position: "relative" }}>
               {isLoadingWinners && (
                    <div className="k-loading-mask" style={{ zIndex: 10 }}>
                         <span className="k-loading-text">Loading</span>
                         <div className="k-loading-image" />
                         <div className="k-loading-color" />
                    </div>
               )}
               <Grid
                    data={winners}
                    skip={page.skip}
                    take={page.take}
                    total={totalCount}
                    sort={sort}
                    sortable={true}
                    fixedScroll={true}
                    className="sba-area-data-grid"
                    pageable={{
                         buttonCount: 4,
                         pageSizes: [10, 25, 50],
                    }}
                    onPageChange={(event) => setPage(event.page)}
                    onSortChange={(event) => {
                         setSort(event.sort);
                         setPage(INITIAL_PAGE);
                    }}
                    rowRender={(row, rowProps) =>
                         React.cloneElement(row, {
                              ...row.props,
                              style: { ...row.props.style, cursor: "pointer" },
                              onClick: () =>
                                   openWinnerDetails(rowProps.dataItem),
                         })
                    }
               >
                    <GridColumn field="name" title="Winner" width="200px" />
                    <GridColumn field="email" title="Email" width="260px" />
                    <GridColumn
                         field="dealerName"
                         title="Dealer"
                         width="240px"
                    />
                    <GridColumn
                         field="retailArea"
                         title="Retail Area"
                         width="140px"
                    />
                    <GridColumn field="jobRole" title="Role" width="180px" />
                    <GridColumn field="weeks" title="Week" width="140px" />
                    <GridColumn
                         field="activationStatus"
                         title="Activation"
                         width="160px"
                    />
                    <GridColumn
                         title="Edit"
                         width="110px"
                         sortable={false}
                         cell={(cellProps) => (
                              <td>
                                   <Button
                                        type="button"
                                        fillMode="flat"
                                        themeColor="primary"
                                        onClick={(event) => {
                                             // The row itself opens the details modal, so the
                                             // click must not travel any further.
                                             event.stopPropagation();
                                             openEditWinner(cellProps.dataItem);
                                        }}
                                   >
                                        Edit
                                   </Button>
                              </td>
                         )}
                    />
               </Grid>
               </div>
          </div>
     );
};

export default RetailWinnersGrid;
